import { useState } from 'react';
import type { Book } from '../../types';
import type { ImportBookItem } from '../../components/BatchImportManager';
import { checkExportPassword, MIN_EXPORT_PASSWORD_LENGTH, probeImportContent } from '../../utils/encryption';
import { parseWordDocument, buildWordImportPayload } from '../../utils/wordImport';
import { notifyError, notifyWarn, errorMessage } from '../../utils/notify';

interface ImportWorkEntry {
  book?: { title?: string; [key: string]: unknown };
  work?: { title?: string; [key: string]: unknown };
  workInfo?: { title?: string; [key: string]: unknown };
  [key: string]: unknown;
}

interface ImportData {
  type?: string;
  books?: ImportWorkEntry[];
  works?: ImportWorkEntry[];
  book?: { title?: string; [key: string]: unknown };
  work?: { title?: string; [key: string]: unknown };
  workInfo?: { title?: string; [key: string]: unknown };
  [key: string]: unknown;
}

interface UseExportImportOptions {
  books: Book[];
  loadBooks: () => Promise<void>;
  setNewlyImportedBookId: (id: string | null) => void;
  breatheTimerRef: React.MutableRefObject<NodeJS.Timeout | null>;
}

export function useExportImport({
  books,
  loadBooks,
  setNewlyImportedBookId,
  breatheTimerRef,
}: UseExportImportOptions) {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordModalTitle, setPasswordModalTitle] = useState('');
  const [passwordModalMessage, setPasswordModalMessage] = useState('');
  const [passwordModalCallback, setPasswordModalCallback] = useState<((password: string) => void) | null>(null);
  const [passwordModalError, setPasswordModalError] = useState('');

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateBookTitle, setDuplicateBookTitle] = useState('');
  const [importingBookData, setImportingBookData] = useState<ImportData | null>(null);

  const [showRenameImport, setShowRenameImport] = useState(false);
  const [renameImportOriginalTitle, setRenameImportOriginalTitle] = useState('');

  const [pendingNewBookId, setPendingNewBookId] = useState<string | null>(null);
  const [, setPendingNewBookIds] = useState<string[]>([]);

  const [showBatchImportManager, setShowBatchImportManager] = useState(false);
  const [batchImportBooks, setBatchImportBooks] = useState<ImportBookItem[]>([]);
  const [pendingBatchImportData, setPendingBatchImportData] = useState<ImportData | null>(null);

  const handleExportBook = async (selectedBooks: string[], onExitDeleteMode?: () => void) => {
    if (selectedBooks.length === 0) return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (window.api as any).exportBook(selectedBooks);
      if (!result.success) {
        notifyError('导出失败：' + result.error);
        return;
      }

      setPasswordModalTitle('设置备份密码');
      setPasswordModalMessage(`请设置备份文件的密码（至少 ${MIN_EXPORT_PASSWORD_LENGTH} 位）。密码用于保护作品数据，忘记后无法恢复。`);
      setPasswordModalError('');
      setPasswordModalCallback(() => async (password: string) => {
        const weak = checkExportPassword(password);
        if (weak) {
          setPasswordModalError(weak);
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const encryptResult = await (window.api as any).encryptExportData(result.data, password);
        if (!encryptResult.success) {
          notifyError('加密失败：' + encryptResult.error);
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const saveResult = await (window.api as any).saveExportFile(encryptResult.data);
        if (saveResult.success) {
          setShowPasswordModal(false);
          setPasswordModalCallback(null);
          setShowSuccessModal(true);
          setSuccessTitle('导出成功');
          setSuccessMessage('您的作品已成功导出并加密保存');

          setTimeout(() => {
            setShowSuccessModal(false);
            onExitDeleteMode?.();
          }, 2000);
        } else if (!saveResult.canceled) {
          // 区分"宿主阻止下载"与其它失败，给出可操作指引
          if (saveResult.error === 'DOWNLOAD_BLOCKED') {
            notifyError(
              '当前环境不允许文件下载（导出文件未能落盘）。请改用外部浏览器打开本项目后再备份。',
              { label: '我知道了', onClick: () => { /* 仅关闭 */ } },
            );
          } else {
            notifyError('保存文件失败：' + saveResult.error);
          }
        }
      });
      setShowPasswordModal(true);
    } catch (error) {
      notifyError('导出过程出错：' + errorMessage(error));
    }
  };

  const handleImportBook = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fileResult = await (window.api as any).importBookFile();
      if (!fileResult.success) {
        if (!fileResult.canceled) notifyError('打开文件失败：' + fileResult.error);
        return;
      }

      // Word 稿件：走独立路径（只取章节标题与正文，无结构可还原）
      if (fileResult.kind === 'word') {
        await importWordFile(fileResult.binary as ArrayBuffer, fileResult.fileName as string);
        return;
      }

      // 先判断文件是否需要口令。
      // 此前这里**无条件**弹密码框，导致"导出时没设过密码"的明文文件
      // 也被要求输入密码 —— 而那个密码不存在，用户只能反复试。
      const probe = probeImportContent(fileResult.data ?? '');

      if (probe.kind === 'not-an-export') {
        notifyError(probe.message);
        return;
      }

      if (probe.kind === 'plaintext') {
        // 未加密的导出数据：直接进入导入流程，不询问密码
        await proceedWithImportData(probe.data as unknown as ImportData);
        return;
      }

      // 确实是加密包（v1/v2）才要求输入口令
      setPasswordModalTitle('输入导入密码');
      setPasswordModalMessage(
        probe.format === 'v2'
          ? '该文件已加密，请输入导出时设置的密码'
          : '该文件使用旧版加密格式，请输入导出时设置的密码',
      );
      setPasswordModalError('');
      setPasswordModalCallback(() => async (password: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const decryptResult = await (window.api as any).decryptImportData(fileResult.data, password);
        if (!decryptResult.success) {
          // 区分"密码错误"与"文件损坏/格式不符"，避免把损坏文件误导成口令问题
          setPasswordModalError(decryptResult.error || '密码错误，请重试');
          return;
        }

        setShowPasswordModal(false);
        setPasswordModalCallback(null);
        await proceedWithImportData(decryptResult.data as ImportData);
      });
      setShowPasswordModal(true);
    } catch (error) {
      notifyError('导入过程出错：' + errorMessage(error));
    }
  };

  /**
   * 导入 Word 稿件。
   *
   * 与 .xts 备份的区别：Word 里**没有结构数据**（卷、人物、势力、设定、伏笔），
   * 因此只导入「章节标题 + 正文」，并且全部放在一个新卷下。
   * 这是为了把其他写作软件里的稿子搬进来。
   */
  const importWordFile = async (binary: ArrayBuffer, fileName: string) => {
    let parsed;
    try {
      parsed = await parseWordDocument(binary);
    } catch (err) {
      notifyError(
        `无法读取该 Word 文档：${errorMessage(err)}。` +
        '若文件是旧版 .doc 格式，请先用 Word 另存为 .docx 再试。',
      );
      return;
    }

    const chapterCount = parsed.volumes.reduce((n, v) => n + v.chapters.length, 0);
    if (chapterCount === 0) {
      notifyError('该 Word 文档里没有找到任何正文内容');
      return;
    }

    // 书名：文档首个标题优先，否则用文件名（去掉扩展名）
    const fallbackTitle = fileName.replace(/\.(docx|doc)$/i, '') || '导入的稿件';
    const bookTitle = parsed.bookTitle || fallbackTitle;

    const importedData = buildWordImportPayload(bookTitle, parsed.volumes, {
      existingTitles: books.map(b => b.title),
    });

    if (parsed.singleChapter) {
      notifyWarn(`未能识别章节标题，已将整篇作为一章导入（书名：${bookTitle}）`);
    }

    await proceedWithImportData(importedData as unknown as ImportData);
  };

  /** 拿到（已解密的）导入数据后：判断批量/单个、重名、再交给服务端 */
  const proceedWithImportData = async (importedData: ImportData, opts?: { notifyDuplicateAsCopy?: boolean }) => {
    const existingTitles = books.map(b => b.title);
    const importedWorks = importedData.books || importedData.works;
    const isMultipleWorks = importedData.type === 'xiaotianshu_batch_export' && Array.isArray(importedWorks);

    if (isMultipleWorks) {
      const workItems: ImportBookItem[] = (importedWorks as ImportWorkEntry[]).map((workData: ImportWorkEntry) => {
        const title = workData.book?.title || workData.work?.title || workData.workInfo?.title || '未命名作品';
        const isDuplicate = existingTitles.includes(title);
        return { title, isDuplicate, newTitle: isDuplicate ? title + '_副本' : title };
      });

      setBatchImportBooks(workItems);
      setPendingBatchImportData(importedData);
      setShowBatchImportManager(true);
      return;
    }

    const importedBookTitle = importedData.book?.title || importedData.work?.title || importedData.workInfo?.title;

    if (existingTitles.includes(importedBookTitle)) {
      setDuplicateBookTitle(importedBookTitle);
      setImportingBookData(importedData);
      setShowDuplicateConfirm(true);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const importResult = await (window.api as any).importBookData(importedData);
    if (importResult.success) {
      await loadBooks();
      setPendingNewBookId(importResult.bookId);
      setShowSuccessModal(true);
      setSuccessTitle('导入成功');
      setSuccessMessage(importResult.skippedChapters > 0
        ? `导入成功，但有 ${importResult.skippedChapters} 个章节因缺少所属分卷信息被跳过`
        : '新作品已添加到您的书库');
    } else {
      notifyError('导入失败：' + importResult.error);
    }
  };

  const handleConfirmImportDuplicate = () => {
    setShowDuplicateConfirm(false);
    setRenameImportOriginalTitle(duplicateBookTitle);
    setShowRenameImport(true);
  };

  const handleCancelImportDuplicate = () => {
    setShowDuplicateConfirm(false);
    setImportingBookData(null);
  };

  const handleRenameImportConfirm = async (newTitle: string) => {
    if (!importingBookData) return;
    const updatedData = { ...importingBookData };
    if (updatedData.book) updatedData.book.title = newTitle;
    else if (updatedData.work) updatedData.work.title = newTitle;
    else if (updatedData.workInfo) updatedData.workInfo.title = newTitle;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const importResult = await (window.api as any).importBookData(updatedData);
    if (importResult.success) {
      await loadBooks();
      setPendingNewBookId(importResult.bookId);
      setShowRenameImport(false);
      setImportingBookData(null);
      setShowSuccessModal(true);
      setSuccessTitle('导入成功');
      setSuccessMessage(importResult.skippedChapters > 0
        ? `导入成功，但有 ${importResult.skippedChapters} 个章节因缺少所属分卷信息被跳过`
        : '新作品已添加到您的书库');
    } else {
      notifyError('导入失败：' + importResult.error);
    }
  };

  const handleRenameImportCancel = () => {
    setShowRenameImport(false);
    setImportingBookData(null);
  };

  const handleBatchImportConfirm = async (updatedBooks: ImportBookItem[]) => {
    const newBookIds: string[] = [];
    let importError = false;
    const batchSources = pendingBatchImportData?.books ?? pendingBatchImportData?.works ?? [];
    for (let i = 0; i < batchSources.length; i++) {
      const workData = batchSources[i] as Record<string, unknown>;
      const updatedItem = updatedBooks[i];
      const updatedData = { ...workData } as Record<string, unknown>;
      if (updatedData.book) (updatedData.book as Record<string, unknown>).title = updatedItem.newTitle;
      else if (updatedData.work) (updatedData.work as Record<string, unknown>).title = updatedItem.newTitle;
      else if (updatedData.workInfo) (updatedData.workInfo as Record<string, unknown>).title = updatedItem.newTitle;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const importResult = await (window.api as any).importBookData(updatedData);
        if (importResult.success) newBookIds.push(importResult.bookId);
        else importError = true;
      } catch {
        importError = true;
      }
    }
    await loadBooks();
    setShowBatchImportManager(false);
    setBatchImportBooks([]);
    setPendingBatchImportData(null);
    if (importError) {
      notifyError('部分作品导入失败，请重试或查看日志');
    } else {
      setShowSuccessModal(true);
      setSuccessTitle('批量导入成功');
      setSuccessMessage('所有作品已添加到您的书库');
      setPendingNewBookIds(newBookIds);
    }
  };

  const handleBatchImportCancel = () => {
    setShowBatchImportManager(false);
    setBatchImportBooks([]);
    setPendingBatchImportData(null);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    if (pendingNewBookId !== null) {
      setNewlyImportedBookId(pendingNewBookId);
      setPendingNewBookId(null);
      if (breatheTimerRef.current) clearTimeout(breatheTimerRef.current);
      breatheTimerRef.current = setTimeout(() => setNewlyImportedBookId(null), 2000);
    }
  };

  return {
    showPasswordModal, passwordModalTitle, passwordModalMessage, passwordModalCallback, passwordModalError,
    setShowPasswordModal, setPasswordModalCallback, setPasswordModalError,
    showSuccessModal, successTitle, successMessage,
    setShowSuccessModal, setSuccessTitle, setSuccessMessage,
    showDuplicateConfirm, duplicateBookTitle,
    showRenameImport, renameImportOriginalTitle,
    importingBookData,
    pendingNewBookId, setPendingNewBookId,
    showBatchImportManager, batchImportBooks,
    handleExportBook,
    handleImportBook,
    handleConfirmImportDuplicate, handleCancelImportDuplicate,
    handleRenameImportConfirm, handleRenameImportCancel,
    handleBatchImportConfirm, handleBatchImportCancel,
    handleSuccessModalClose,
  };
}
